<template>
  <section
    ref="snapshotViewer"
    class="snapshot-viewer"
    :class="{
      'has-constrained-overlay-layout': hasConstrainedOverlayLayout && !mobileLayout,
      'is-mobile-layout': mobileLayout
    }"
    data-unit="three-d-snapshot-viewer"
    :aria-label="snapshot.snapshotName"
  >
    <library-autodesk-viewer
      ref="autodeskViewer"
      :library-enable-explode="libraryEnableExplode"
      :library-mobile-layout="mobileLayout"
      library-mode
      :library-part-number-property="snapshot.partNumberProperty"
      :library-selection-redirects="lockedAssemblySelectionRedirects"
      :library-show-advanced-selection-controls="libraryShowAdvancedSelectionControls"
      @error="emitViewerEvent('error')"
      @ready="emitViewerEvent('viewerShellReady')"
      @selection-activated="handleSelectionActivated"
      @selection-change="handleSelectionChange"
    />
    <div class="viewer-overlays">
      <three-d-viewer-part-search
        v-if="isAssetReady && partSearchAvailable"
        ref="partSearch"
        :available="partSearchAvailable"
        :snapshot-name="snapshot.snapshotName"
        :source="partSearchSource"
        @clear-focus="clearPartSearchFocus"
        @focus="focusPartSearchResult"
      />
      <div
        v-if="cardElement && cardOpen"
        class="part-card-stack"
      >
        <three-d-part-details-card
          :element="cardElement"
          :part="cardPart"
          :pricing-loaded="pricingRequestComplete"
          :status="cardStatus"
          @close="closePartDetailsCard"
          @retry="retryPartDetails"
        />
        <three-d-assembly-orderable-parts-card
          v-if="orderableAssemblyParts.length > 0"
          :assembly-part-number="visibleAssemblyPartNumber"
          :items="orderableAssemblyParts"
          :ordering-mode-active="assemblyOrderingModeActive"
          :selected-part-key="selectedAssemblyChildPartKey"
          @select-part="selectAssemblyChildPart"
          @view-parts="activateAssemblyOrderingMode"
        />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import AutodeskViewer from '@/components/admin/content/editor/three-d-model/AutodeskViewer.vue'
import type {
  ViewerSelectedModelElement,
  ViewerSelectionPayload
} from '@/components/admin/content/editor/three-d-model/AutodeskViewer.vue'
import ThreeDAssemblyOrderablePartsCard from './ThreeDAssemblyOrderablePartsCard.vue'
import ThreeDPartDetailsCard from './ThreeDPartDetailsCard.vue'
import ThreeDViewerPartSearch from './ThreeDViewerPartSearch.vue'
import {
  useThreeDLockedAssemblyOrdering
} from './useThreeDLockedAssemblyOrdering'
import type {
  ThreeDAssemblyOrderingViewerApi
} from './useThreeDLockedAssemblyOrdering'
import { getMappedThreeDPart } from '@/controllers/library/threeDPartMapping'
import type {
  Part,
  ThreeDViewerPartSearchResult,
  ThreeDViewerPartSearchSource,
  ThreeDViewerPartSearchSourcePayload
} from '@/interfaces/library'
import type { MediaThreeDModelSnapshotDto } from '@/interfaces/admin/threeDModel'

type AutodeskViewerApi = ThreeDAssemblyOrderingViewerApi & {
  clearLibrarySearchFocus: () => void
  setLibraryAssetReady: () => boolean
  setLibrarySearchFocus: (elementIds: number[]) => boolean
}

type ViewerPartSearchApi = {
  clear: () => void
  clearResultSelection: () => void
  setQuery: (value: string) => void
}

const store = useStore()
const CONSTRAINED_OVERLAY_WIDTH = 1144
const LibraryAutodeskViewer = AutodeskViewer
const libraryEnableExplode = computed(() => store.getters['user/library3dEnableExplode'])
const libraryShowAdvancedSelectionControls = computed(() =>
  store.getters['user/library3dShowAdvancedSelectionControls']
)
const viewerActiveContent = computed(() =>
  store.state.content.toc?.viewerActiveContent ?? '3d'
)
const autodeskViewer = ref<AutodeskViewerApi | null>(null)
const snapshotViewer = ref<HTMLElement | null>(null)
const partSearch = ref<ViewerPartSearchApi | null>(null)
const hasConstrainedOverlayLayout = ref(false)
const isAssetReady = ref(false)
const partSearchAvailable = ref(false)
const partSearchSource = ref<ThreeDViewerPartSearchSource[]>([])
const snapshotLockedAssemblyParentIds = ref<Record<number, number>>({})
const focusedPartSearchResult = ref<ThreeDViewerPartSearchResult | null>(null)
const focusedPartDetailsSource = ref<ThreeDViewerPartSearchSource | null>(null)
const selectedElement = ref<ViewerSelectedModelElement | null>(null)
const mappedPart = ref<Part | null>(null)
const pricingRequestComplete = ref(false)
const selectionStatus = ref<'error' | 'idle' | 'loading' | 'ready' | 'unavailable'>('idle')
const cardOpen = ref(false)
let mappingRequestId = 0
let pendingPartSearchQuery: string | null = null
let pendingRedirectSelectionDbId: number | null = null
let overlayResizeObserver: ResizeObserver | null = null

const focusedPartElement = computed<ViewerSelectedModelElement | null>(() => {
  const source = focusedPartDetailsSource.value
  if (!source) return null

  return {
    description: source.part.description ?? null,
    id: String(source.elementIds[0] ?? source.part.id ?? source.part.partNumber ?? ''),
    mappedPart: true,
    name: source.part.name ?? source.part.partNumber ?? String(source.part.id ?? ''),
    partNumber: source.part.partNumber ?? null
  }
})
const cardElement = computed(() => selectedElement.value ?? focusedPartElement.value)
const cardPart = computed(() => mappedPart.value)
const cardStatus = computed(() => selectionStatus.value)
const {
  activateMode: activateAssemblyOrderingMode,
  clearMode: clearAssemblyOrderingMode,
  handleDirectSelection: handleAssemblyOrderingDirectSelection,
  orderingModeActive: assemblyOrderingModeActive,
  parts: orderableAssemblyParts,
  reset: resetAssemblyOrdering,
  selectedPartKey: selectedAssemblyChildPartKey,
  selectionRedirects: lockedAssemblySelectionRedirects,
  selectPart: selectAssemblyChildPart,
  visibleAssemblyPartNumber
} = useThreeDLockedAssemblyOrdering({
  cardOpen,
  mappedPart,
  partSearchSource,
  selectedElement,
  snapshotLockedAssemblyParentIds,
  viewer: autodeskViewer,
  viewerActiveContent
})

const props = defineProps<{
  attemptId: number
  mobileLayout?: boolean
  snapshot: MediaThreeDModelSnapshotDto
}>()

const mobileLayout = computed(() => props.mobileLayout === true)

const emit = defineEmits([
  'assetReady',
  'error',
  'partSearchClear',
  'partSearchFocus',
  'selectionChange',
  'viewerShellReady'
])

type ViewerEventName = 'assetReady' | 'error' | 'viewerShellReady'

function emitViewerEvent (event: ViewerEventName) {
  emit(event, {
    attemptId: props.attemptId,
    snapshotId: props.snapshot.id
  })
}

function setAssetReady () {
  if (autodeskViewer.value?.setLibraryAssetReady()) {
    isAssetReady.value = true
    emitViewerEvent('assetReady')
  }
}

function setPartSearchSource ({
  attemptId,
  lockedAssemblyParentIds = {},
  snapshotId,
  source
}: ThreeDViewerPartSearchSourcePayload) {
  if (attemptId !== props.attemptId || snapshotId !== props.snapshot.id) return false

  resetPartSearchContext()
  partSearchSource.value = source
  snapshotLockedAssemblyParentIds.value = lockedAssemblyParentIds
  partSearchAvailable.value = true
  return true
}

function applyPendingPartSearchQuery () {
  if (pendingPartSearchQuery === null || !partSearch.value) return

  partSearch.value.setQuery(pendingPartSearchQuery)
  pendingPartSearchQuery = null
}

function setViewerPartSearchQuery (query: string) {
  pendingPartSearchQuery = query
  applyPendingPartSearchQuery()
}

watch(partSearch, applyPendingPartSearchQuery)

function closePartDetailsCard () {
  clearAssemblyOrderingMode(true)
  cardOpen.value = false
}

function getSearchResultPartDetailsSource (result: ThreeDViewerPartSearchResult) {
  const lockedParentIds: number[] = []
  for (const elementId of result.elementIds) {
    const directParentId = getLockedAssemblyParentId(elementId)
    if (directParentId === null) continue

    const resolvedParentId = resolveLockedAssemblyParentId(directParentId)
    if (resolvedParentId === null) return null
    lockedParentIds.push(resolvedParentId)
  }
  if (lockedParentIds.length === 0) return result

  const uniqueParentIds = [...new Set(lockedParentIds)]
  if (uniqueParentIds.length !== 1) return null

  const parentId = uniqueParentIds[0]
  const parentSource = partSearchSource.value.find(({ elementIds, part }) =>
    part.deleted !== true && elementIds.includes(parentId)
  )
  if (!parentSource) return null

  return {
    ...parentSource,
    elementIds: [parentId, ...parentSource.elementIds.filter(elementId => elementId !== parentId)]
  }
}

function focusPartSearchResult (result: ThreeDViewerPartSearchResult) {
  clearAssemblyOrderingMode()
  const requestId = ++mappingRequestId
  const partDetailsSource = getSearchResultPartDetailsSource(result)
  selectedElement.value = null
  mappedPart.value = null
  pricingRequestComplete.value = false
  focusedPartSearchResult.value = result
  focusedPartDetailsSource.value = partDetailsSource
  selectionStatus.value = partDetailsSource ? 'loading' : 'idle'
  cardOpen.value = !!partDetailsSource
  autodeskViewer.value?.setLibrarySearchFocus(result.elementIds)
  if (partDetailsSource) void loadMappedPart(partDetailsSource.part, requestId)
  emit('partSearchFocus', result)
}

function clearPartSearchFocus () {
  focusedPartSearchResult.value = null
  focusedPartDetailsSource.value = null
  if (!selectedElement.value) {
    mappingRequestId++
    mappedPart.value = null
    pricingRequestComplete.value = false
    selectionStatus.value = 'idle'
    cardOpen.value = false
  }
  autodeskViewer.value?.clearLibrarySearchFocus?.()
  emit('partSearchClear')
}

function clearPartSearchResultSelection () {
  focusedPartSearchResult.value = null
  focusedPartDetailsSource.value = null
  partSearch.value?.clearResultSelection()
  autodeskViewer.value?.clearLibrarySearchFocus?.()
  emit('partSearchClear')
}

function resetPartSearchContext () {
  mappingRequestId++
  resetAssemblyOrdering()
  partSearch.value?.clear()
  autodeskViewer.value?.clearLibrarySearchFocus?.()
  focusedPartSearchResult.value = null
  focusedPartDetailsSource.value = null
  selectedElement.value = null
  mappedPart.value = null
  pricingRequestComplete.value = false
  selectionStatus.value = 'idle'
  cardOpen.value = false
  pendingRedirectSelectionDbId = null
  autodeskViewer.value?.selectElements?.([])
}

function resetSnapshotContext () {
  resetPartSearchContext()
  pendingPartSearchQuery = null
  isAssetReady.value = false
  partSearchAvailable.value = false
  partSearchSource.value = []
  snapshotLockedAssemblyParentIds.value = {}
}

watch(
  [() => props.attemptId, () => props.snapshot.id],
  resetSnapshotContext
)

function getLockedAssemblyParentId (dbId: number) {
  return lockedAssemblySelectionRedirects.value[dbId] ?? null
}

function resolveLockedAssemblyParentId (dbId: number) {
  const visited = new Set<number>()
  let parentId = dbId

  while (!visited.has(parentId)) {
    visited.add(parentId)
    const nextParentId = getLockedAssemblyParentId(parentId)
    if (nextParentId === null) return parentId
    parentId = nextParentId
  }

  return null
}

function getSnapshotMappedPart (element: ViewerSelectedModelElement) {
  const dbId = Number(element.id)
  if (!Number.isInteger(dbId)) return null

  return partSearchSource.value.find(({ elementIds, part }) =>
    part.deleted !== true && elementIds.includes(dbId)
  )?.part ?? null
}

function isFocusedPartSearchElement (dbId: number) {
  const result = focusedPartSearchResult.value
  if (!result) return false

  return result.elementIds.includes(dbId) ||
    focusedPartDetailsSource.value?.elementIds.includes(dbId) === true ||
    Object.values(result.lockedAssemblyParentIds ?? {}).includes(dbId)
}

function handleSelectionEnrichment (payload: ViewerSelectionPayload) {
  const currentElement = selectedElement.value
  const enrichedElement = payload.count === 1 ? payload.element : null
  if (!currentElement || !enrichedElement || currentElement.id !== enrichedElement.id) return

  const shouldMapEnrichedElement =
    !currentElement.partNumber &&
    !!enrichedElement.partNumber &&
    !partSearchAvailable.value &&
    !mappedPart.value &&
    selectionStatus.value !== 'loading'

  selectedElement.value = enrichedElement
  if (shouldMapEnrichedElement) void mapSelectedElement()
}

function handleSelectionChange (payload: ViewerSelectionPayload) {
  emit('selectionChange', payload)
  if (payload.propertyEnrichment) {
    handleSelectionEnrichment(payload)
    return
  }
  const selectedDbId = payload.count === 1 ? payload.selectedDbIds[0] : undefined
  const redirectedDirectSelection =
    !payload.directSelection &&
    pendingRedirectSelectionDbId !== null &&
    selectedDbId === pendingRedirectSelectionDbId
  if (payload.directSelection || redirectedDirectSelection) {
    pendingRedirectSelectionDbId = null
  }
  if (!payload.directSelection && !redirectedDirectSelection) return

  if (payload.directSelection && handleAssemblyOrderingDirectSelection(selectedDbId)) return

  if (payload.directSelection &&
    selectedDbId !== undefined &&
    focusedPartSearchResult.value &&
    !isFocusedPartSearchElement(selectedDbId)) {
    clearPartSearchResultSelection()
  }

  const lockedAssemblyParentId = selectedDbId === undefined
    ? null
    : getLockedAssemblyParentId(selectedDbId)
  if (lockedAssemblyParentId !== null) {
    mappingRequestId++
    selectedElement.value = null
    mappedPart.value = null
    pricingRequestComplete.value = false
    selectionStatus.value = 'idle'
    cardOpen.value = false
    pendingRedirectSelectionDbId = lockedAssemblyParentId
    autodeskViewer.value?.selectElements?.([String(lockedAssemblyParentId)])
    return
  }

  mappingRequestId++
  selectedElement.value = payload.count === 1 ? payload.element : null
  mappedPart.value = null
  pricingRequestComplete.value = false
  cardOpen.value = !!selectedElement.value

  if (!selectedElement.value) {
    selectionStatus.value = 'idle'
    return
  }

  const snapshotMappedPart = getSnapshotMappedPart(selectedElement.value)
  if (snapshotMappedPart) {
    const requestId = ++mappingRequestId
    selectionStatus.value = 'loading'
    void loadMappedPart(snapshotMappedPart, requestId)
    return
  }

  if (partSearchAvailable.value) {
    // The snapshot-scoped source is authoritative, so absence confirms that
    // the selected model element has no mapped Part.
    selectionStatus.value = 'ready'
    return
  }

  mapSelectedElement()
}

function handleSelectionActivated (dbId: number) {
  if (selectedElement.value?.id === String(dbId)) cardOpen.value = true
}

function retryPartDetails () {
  if (selectedElement.value) {
    void mapSelectedElement()
    return
  }

  const partDetailsSource = focusedPartDetailsSource.value
  if (!partDetailsSource) return

  const requestId = ++mappingRequestId
  mappedPart.value = null
  pricingRequestComplete.value = false
  selectionStatus.value = 'loading'
  void loadMappedPart(partDetailsSource.part, requestId)
}

async function mapSelectedElement () {
  const element = selectedElement.value
  const supplierKey = props.snapshot.supplierKey
  if (!element?.partNumber) {
    selectionStatus.value = 'ready'
    return
  }
  // The snapshot supplier belongs to the future Library 3D snapshot contract.
  // Without it, Part Number alone is not sufficient to determine a mapping.
  if (!supplierKey) {
    selectionStatus.value = 'unavailable'
    return
  }

  const requestId = ++mappingRequestId
  selectionStatus.value = 'loading'
  try {
    const part = await getMappedThreeDPart(element.partNumber, supplierKey)
    if (requestId !== mappingRequestId) return
    if (!part) {
      // Library search may omit restricted Parts, so a missing result cannot
      // confirm that the selected element is unmapped.
      selectionStatus.value = 'unavailable'
      return
    }
    await loadMappedPart(part, requestId)
  } catch {
    if (requestId === mappingRequestId) selectionStatus.value = 'error'
  }
}

async function loadMappedPart (part: Part & { visible?: boolean }, requestId: number) {
  if (requestId !== mappingRequestId) return

  mappedPart.value = part
  pricingRequestComplete.value = false
  selectionStatus.value = 'ready'
  if (!part.orderable || part.visible === false) return

  try {
    const pricing = await store.dispatch('content/getPricingWithReturn', {
      ...part,
      mediaIdentifier: store.state.content.toc?.book?.identifier ?? '',
      pricing: part,
      type: 'part'
    })
    if (requestId !== mappingRequestId) return
    mappedPart.value = { ...part, ...pricing }
  } catch {
    // Pricing is optional enrichment; preserve the mapped Part identity.
  } finally {
    // A failed pricing lookup must not permanently gate cart controls.
    if (requestId === mappingRequestId) pricingRequestComplete.value = true
  }
}

onMounted(() => {
  const element = snapshotViewer.value
  if (!element || typeof ResizeObserver === 'undefined') return

  overlayResizeObserver = new ResizeObserver(entries => {
    const width = entries[0]?.contentRect.width ?? 0
    if (width > 0) hasConstrainedOverlayLayout.value = width <= CONSTRAINED_OVERLAY_WIDTH
  })
  overlayResizeObserver.observe(element)
})

onBeforeUnmount(() => {
  overlayResizeObserver?.disconnect()
  mappingRequestId++
  resetAssemblyOrdering()
  autodeskViewer.value?.clearLibrarySearchFocus?.()
  pendingPartSearchQuery = null
  partSearchSource.value = []
  snapshotLockedAssemblyParentIds.value = {}
  focusedPartSearchResult.value = null
  focusedPartDetailsSource.value = null
})

defineExpose({ setAssetReady, setPartSearchSource, setViewerPartSearchQuery })
</script>

<style scoped>
.snapshot-viewer {
  background: #f5f6f7;
  display: flex;
  flex: 1;
  min-height: 360px;
  overflow: hidden;
  position: relative;
}

.snapshot-viewer :deep(.autodesk-viewer-layout) {
  flex: 1;
}

.viewer-overlays {
  display: contents;
}

.part-card-stack {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: calc(100% - 1.5rem);
  overflow-y: auto;
  position: absolute;
  right: 0.75rem;
  top: 0.75rem;
  width: min(21rem, calc(100% - 1.5rem));
  z-index: 4;
}

.part-card-stack :deep(.three-d-part-card) {
  flex: none;
  max-height: none;
  position: static;
  width: 100%;
}

.snapshot-viewer.has-constrained-overlay-layout .viewer-overlays {
  align-content: flex-start;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  left: 0.75rem;
  max-height: calc(100% - 1.5rem);
  overflow-y: auto;
  pointer-events: none;
  position: absolute;
  right: 0.75rem;
  top: 0.75rem;
  z-index: 4;
}

.snapshot-viewer.has-constrained-overlay-layout :deep(.viewer-part-search) {
  flex: 1 1 28rem;
  left: auto;
  max-width: 28rem;
  min-width: min(20rem, 100%);
  pointer-events: auto;
  position: relative;
  top: auto;
  transform: none;
  width: auto;
}

.snapshot-viewer.has-constrained-overlay-layout .part-card-stack {
  flex: 0 1 21rem;
  margin-left: auto;
  max-height: none;
  overflow-y: visible;
  pointer-events: auto;
  position: relative;
  right: auto;
  top: auto;
  width: auto;
}

.snapshot-viewer.is-mobile-layout {
  background: transparent;
  display: flex;
  flex: none;
  flex-direction: column;
  gap: 0.75rem;
  min-height: 0;
  min-width: 0;
  overflow: visible;
}

.snapshot-viewer.is-mobile-layout :deep(.autodesk-viewer-layout) {
  flex: none;
  height: auto;
  min-height: 0;
  min-width: 0;
  order: 2;
  width: 100%;
}

.snapshot-viewer.is-mobile-layout :deep(.autodesk-viewer) {
  flex: none;
  height: clamp(22.5rem, calc(60vh - 3.25rem), 32.75rem);
  min-height: 22.5rem;
}

.snapshot-viewer.is-mobile-layout :deep(.viewer-part-search) {
  left: auto;
  max-width: none;
  order: 1;
  position: relative;
  top: auto;
  transform: none;
  width: 100%;
}

.snapshot-viewer.is-mobile-layout :deep(.viewer-part-search .control.is-expanded) {
  min-width: 0;
}

.snapshot-viewer.is-mobile-layout :deep(.viewer-part-search .button) {
  min-height: 2.75rem;
  min-width: 2.75rem;
}

.snapshot-viewer.is-mobile-layout :deep(.viewer-part-search .viewer-part-results) {
  max-height: min(14rem, 40vh);
}

.snapshot-viewer.is-mobile-layout :deep(.viewer-part-search .viewer-part-result) {
  min-height: 2.75rem;
  min-width: 0;
  overflow: hidden;
}

.snapshot-viewer.is-mobile-layout :deep(.viewer-part-search .part-number) {
  max-width: 40%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.snapshot-viewer.is-mobile-layout .part-card-stack {
  max-height: none;
  order: 3;
  overflow: visible;
  position: static;
  width: 100%;
}

.snapshot-viewer.is-mobile-layout .part-card-stack :deep(.three-d-part-card) {
  overflow-x: hidden;
}

.snapshot-viewer.is-mobile-layout .part-card-stack :deep(.close-card) {
  min-height: 2.75rem;
  min-width: 2.75rem;
}

</style>
